import { CometChat } from '@cometchat-pro/chat'
import { isEmpty } from '$utils/objectIsEmpty'
import { doNothing } from '$utils/doNothing'

let cometChatUser

const setUser = user => (cometChatUser = user)

const getUser = () => cometChatUser

let conversation

const setConversation = conv => (conversation = conv)

const getConversation = () => conversation

const initialize = async () => {
  var appID = process.env.REACT_APP_COMETCHAT_APP_ID
  var region = process.env.REACT_APP_COMETCHAT_REGION
  var appSetting = new CometChat.AppSettingsBuilder()
    .subscribePresenceForAllUsers()
    .setRegion(region)
    .autoEstablishSocketConnection(true)
    .build()
  try {
    await CometChat.init(appID, appSetting)
  } catch (error) {
    return error
  }
}

const isInitialized = () => CometChat.isInitialized()

const createUser = async (userId, name) => {
  var apiKey = process.env.REACT_APP_COMETCHAT_API_KEY
  var user = new CometChat.User(userId)
  user.setName(name)

  try {
    await CometChat.createUser(user, apiKey)
  } catch (error) {
    return error
  }
}

const getUserById = async id => {
  try {
    return await CometChat.getUser(id)
  } catch (error) {
    return ''
  }
}

const getLoggedinUser = async () => {
  try {
    return await CometChat.getLoggedinUser()
  } catch (error) {
    return ''
  }
}

const loginUser = async userId => {
  var apiKey = process.env.REACT_APP_COMETCHAT_AUTH_KEY
  try {
    await CometChat.login(userId, apiKey)
  } catch (error) {
    return error
  }
}

const logoutUser = async () => {
  try {
    await CometChat.logout()
  } catch (error) {
    return error
  }
}

const updateUserName = async name => {
  try {
    const user: any = await getLoggedinUser()
    user.setName(name)
    await CometChat.updateCurrentUserDetails(user)
  } catch (error) {
    doNothing()
  }
}

const markAsRead = message => CometChat.markAsRead(message)

const markAsDelivered = async message => {
  try {
    await CometChat.markAsDelivered(message)
  } catch (error) {
    doNothing()
  }
}

const messagesRequestBuilder = (limit: number, id, userType) => {
  if (userType === 'user') {
    return new CometChat.MessagesRequestBuilder()
      .setLimit(limit)
      .setUID(id)
      .build()
  }
  if (userType === 'group') {
    return new CometChat.MessagesRequestBuilder()
      .setLimit(limit)
      .setGUID(id)
      .build()
  }

  return new CometChat.MessagesRequestBuilder()
    .setLimit(limit)
    .setUID(id)
    .build()
}

const conversationsRequestBuilder = (limit: number) =>
  new CometChat.ConversationsRequestBuilder()
    .setLimit(limit)
    .withUserAndGroupTags(true)
    .build()

const textMessage = (receiverId, messageInput, receiverType) =>
  new CometChat.TextMessage(receiverId, messageInput, receiverType)

const sendMessage = async message => await CometChat.sendMessage(message)

const getConversationFromMessage = async message => await CometChat.CometChatHelper.getConversationFromMessage(message)

const addMessageListener = (nameId, messageEventListener) => CometChat.addMessageListener(nameId, messageEventListener)

const removeMessageListener = nameId => CometChat.removeMessageListener(nameId)

const messageListener = messageEvent => new CometChat.MessageListener(messageEvent)

const markLastMessagesAsDelivered = async () => {
  await getLoggedinUser()
  const conversationRequest = conversationsRequestBuilder(30)
  conversationRequest.fetchNext().then((conversationList: any) => {
    conversationList.map(nextConversation => {
      if (nextConversation.lastMessage.hasOwnProperty('deliveredAt') === false) {
        markAsDelivered(nextConversation.lastMessage)
      }
    })
  })
}

const getUnreadMessageCount = async () => {
  try {
    const usersWithUnreadMessages: any = await CometChat.getUnreadMessageCount()
    if (isEmpty(usersWithUnreadMessages)) {
      return false
    }
    if (isEmpty(usersWithUnreadMessages.users) && isEmpty(usersWithUnreadMessages.groups)) {
      return false
    }
    return true
  } catch (error) {
    return false
  }
}

const deleteConversation = async (conversationId, type) => {
  try {
    await CometChat.deleteConversation(conversationId, type)
  } catch (error) {
    throw error
  }
}

const recoverUserFromGroup = async (groupId: string) => {
  const memberLimit = 10
  const groupMemberRequest = new CometChat.GroupMembersRequestBuilder(groupId).setLimit(memberLimit).build()
  try {
    const groupMember: any = await groupMemberRequest.fetchNext()
    const loggedUser: any = await getLoggedinUser()
    const user = groupMember.filter(member => member.uid !== loggedUser.getUid())[0]
    return user
  } catch (error) {
    throw error
  }
}

const createGroup = async (guid: string, name: string, members: string[]) => {
  const groupType = CometChat.GROUP_TYPE.PRIVATE
  try {
    const group = new CometChat.Group(guid, name, groupType)
    const groupMembers = members.map(
      member => new CometChat.GroupMember(member, CometChat.GROUP_MEMBER_SCOPE.PARTICIPANT),
    )
    await CometChat.createGroupWithMembers(group, groupMembers, [])
  } catch (error) {
    throw error
  }
}

const retreiveGroup = async (guid: string) => {
  try {
    const group: any = await CometChat.getGroup(guid)
    return group
  } catch (error) {
    throw error
  }
}

const errors = {
  CHAT_GROUP_NOT_CREATED: 'CHAT_GROUP_NOT_CREATED',
}

interface ChatClient {
  initialize: () => Promise<void>
  isInitialized: () => any
  createUser: (userId: string, name: string) => Promise<void>
  loginUser: (userId: string) => Promise<any>
  getLoggedinUser: () => Promise<any>
  logoutUser: () => Promise<void>
  setUser: (user: any) => any
  getUser: () => any
  getUserById: (id) => any
  updateUserName: (name: string) => Promise<void>
  setConversation: (conversation: any) => any
  getConversation: () => any
  markAsRead: (message) => any
  markAsDelivered: (message) => any
  messagesRequestBuilder: (limit: number, id, userType) => any
  conversationsRequestBuilder: (limit: number) => any
  textMessage: (receiverId, messageInput, receiverType) => any
  sendMessage: (textMessage: any) => Promise<any>
  getConversationFromMessage: (message: any) => Promise<any>
  addMessageListener: (name: string, messageEventListener: any) => void
  removeMessageListener: (name) => void
  messageListener: (messageEvent: {}) => void
  markLastMessagesAsDelivered: () => void
  getUnreadMessageCount: () => any
  deleteConversation: (conversationId: string, type: string) => Promise<any>
  recoverUserFromGroup: (groupId: string) => Promise<any>
  createGroup: (guid: string, name: string, members: string[]) => Promise<any>
  retreiveGroup: (guid: string) => Promise<any>
  errors: any
}

export const chatClient: ChatClient = {
  initialize,
  isInitialized,
  createUser,
  loginUser,
  getLoggedinUser,
  logoutUser,
  setUser,
  getUser,
  getUserById,
  updateUserName,
  setConversation,
  getConversation,
  markAsRead,
  markAsDelivered,
  messagesRequestBuilder,
  conversationsRequestBuilder,
  textMessage,
  sendMessage,
  getConversationFromMessage,
  addMessageListener,
  removeMessageListener,
  messageListener,
  markLastMessagesAsDelivered,
  getUnreadMessageCount,
  deleteConversation,
  recoverUserFromGroup,
  createGroup,
  retreiveGroup,
  errors,
}



// WEBPACK FOOTER //
// ./src/infrastructure/cometChat/cometChatClient.ts